import { supabase } from '@/shared/lib/supabase';

/**
 * Billing-info service — read + write the subset of customer_profile that
 * appears on invoices.
 *
 * RLS-scoped to `auth.uid()` automatically. Anon callers get an error.
 */

export interface BillingInfo {
  fullName: string;
  email: string;            // read-only; sourced from auth.users
  phone: string;
  company: string;
  registrationNumber: string;
  /** Empty string means "not VAT registered" — invoices won't print a VAT line. */
  vatNumber: string;
  billingAddressLine1: string;
  billingAddressLine2: string;
  billingAddressCity: string;
  billingAddressPostalCode: string;
  billingAddressCountry: string;
  /** When set, invoices send to this address instead of the auth email. */
  billingEmail: string;
  /** Optional CC list for invoice emails (e.g. accountant). */
  billingEmailCc: string[];
}

const EMPTY_BILLING_INFO: BillingInfo = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  registrationNumber: '',
  vatNumber: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingAddressCity: '',
  billingAddressPostalCode: '',
  billingAddressCountry: 'ZA',
  billingEmail: '',
  billingEmailCc: [],
};

export const billingInfoService = {
  /** Load the authenticated customer's billing info. */
  async get(): Promise<BillingInfo> {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData.session;
    if (!session) {
      throw new Error('Not signed in.');
    }

    const { data, error } = await supabase
      .from('customer_profile')
      .select(
        'full_name, phone, company, registration_number, vat_number, billing_address_line1, billing_address_line2, billing_address_city, billing_address_postal_code, billing_address_country, billing_email, billing_email_cc'
      )
      .eq('id', session.user.id)
      .maybeSingle();

    if (error) {
      throw new Error(`Could not load billing info: ${error.message}`);
    }

    if (!data) {
      // Profile row not yet created (race with auth trigger).
      return { ...EMPTY_BILLING_INFO, email: session.user.email ?? '' };
    }

    return {
      fullName: data.full_name ?? '',
      email: session.user.email ?? '',
      phone: data.phone ?? '',
      company: data.company ?? '',
      registrationNumber: data.registration_number ?? '',
      vatNumber: data.vat_number ?? '',
      billingAddressLine1: data.billing_address_line1 ?? '',
      billingAddressLine2: data.billing_address_line2 ?? '',
      billingAddressCity: data.billing_address_city ?? '',
      billingAddressPostalCode: data.billing_address_postal_code ?? '',
      billingAddressCountry: data.billing_address_country ?? 'ZA',
      billingEmail: data.billing_email ?? '',
      billingEmailCc: data.billing_email_cc ?? [],
    };
  },

  /** Persist the form values back to customer_profile. RLS enforces ownership. */
  async update(input: BillingInfo): Promise<BillingInfo> {
    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData.session;
    if (!session) {
      throw new Error('Not signed in.');
    }

    const { error } = await supabase
      .from('customer_profile')
      .update({
        full_name: input.fullName.trim() || null,
        phone: input.phone.trim() || null,
        company: input.company.trim() || null,
        registration_number: input.registrationNumber.trim() || null,
        vat_number: input.vatNumber.trim() || null,
        billing_address_line1: input.billingAddressLine1.trim() || null,
        billing_address_line2: input.billingAddressLine2.trim() || null,
        billing_address_city: input.billingAddressCity.trim() || null,
        billing_address_postal_code: input.billingAddressPostalCode.trim() || null,
        billing_address_country: input.billingAddressCountry || 'ZA',
        billing_email: input.billingEmail.trim() || null,
        billing_email_cc: input.billingEmailCc.length > 0 ? input.billingEmailCc : null,
      })
      .eq('id', session.user.id);

    if (error) {
      throw new Error(`Could not save billing info: ${error.message}`);
    }

    return this.get();
  },
};
